import { goSolidApi } from "@/composables/useGoSolidApi";
import type {
  AuthorizedSellerCompanyListResponse,
  DeviceNote,
  AiAutofillResponse,
  CancellationStatement,
  CancellationStatementDeviceLink,
  EcolabelDevice,
  EcolabelDeviceCreateRequest,
  EcolabelDeviceFilters,
  EcolabelDeviceListResponse,
  EcolabelDeviceUpdateRequest,
  DuplicatePair,
  DuplicatePairListResponse,
  FilterOption,
  SubmissionBatchListResponse,
  SubmitDevicesRequest,
  SubmitDevicesResponse,
  IrecDeviceLookupResponse,
  UpdateStatementRequest,
} from "./ecolabelsTypes";

export const ecolabelsApi = {
  // Device CRUD
  listDevices: (
    filters?: EcolabelDeviceFilters,
    offset?: number,
    limit?: number
  ) => {
    const params = new URLSearchParams();
    if (filters?.standard) params.append("standard", filters.standard);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.country) params.append("country", filters.country);
    if (filters?.renewable_type)
      params.append("renewable_type", filters.renewable_type);
    if (filters?.search) params.append("search", filters.search);
    if (offset !== undefined) params.append("offset", String(offset));
    if (limit !== undefined) params.append("limit", String(limit));

    const queryString = params.toString();
    const url = `/api/ecolabels/devices${queryString ? `?${queryString}` : ""}`;
    return goSolidApi.get<EcolabelDeviceListResponse>(url);
  },

  createDevice: (data: EcolabelDeviceCreateRequest) =>
    goSolidApi.post<EcolabelDevice>("/api/ecolabels/devices", data),

  updateDevice: (deviceId: number, data: EcolabelDeviceUpdateRequest) =>
    goSolidApi.put<EcolabelDevice>(`/api/ecolabels/devices/${deviceId}`, data),

  deleteDevice: (deviceId: number) =>
    goSolidApi.delete(`/api/ecolabels/devices/${deviceId}`),

  bulkDeleteDevices: (deviceIds: number[]) =>
    goSolidApi.post<{ deleted_count: number }>(
      "/api/ecolabels/devices/bulk-delete",
      {
        device_ids: deviceIds,
      }
    ),

  // Device submission workflow (DEV-777). The chat endpoint itself is consumed by
  // AiChatCard via its `endpoint` prop: /api/ecolabels/devices/chat.
  submitDevices: (data: SubmitDevicesRequest) =>
    goSolidApi.post<SubmitDevicesResponse>(
      "/api/ecolabels/devices/submit",
      data
    ),

  listSubmissionBatches: (offset = 0, limit = 100) =>
    goSolidApi.get<SubmissionBatchListResponse>(
      `/api/ecolabels/devices/submissions?offset=${offset}&limit=${limit}`
    ),

  listSubmissionBatchDevices: (batchId: number) =>
    goSolidApi.get<{
      devices: EcolabelDevice[];
      pending_duplicate_counts: Record<number, number>;
    }>(`/api/ecolabels/devices/submissions/${batchId}/devices`),

  approveDevice: (deviceId: number, overrideDuplicates = false) =>
    goSolidApi.post<EcolabelDevice>(
      `/api/ecolabels/devices/${deviceId}/approve`,
      { override_duplicates: overrideDuplicates }
    ),

  rejectDevice: (deviceId: number, reason: string) =>
    goSolidApi.post<EcolabelDevice>(
      `/api/ecolabels/devices/${deviceId}/reject`,
      { reason }
    ),

  /** Internal EKOenergy review notes (admin-only; sellers get a 403). */
  listDeviceNotes: (deviceId: number) =>
    goSolidApi.get<{ notes: DeviceNote[] }>(
      `/api/ecolabels/devices/${deviceId}/notes`
    ),

  addDeviceNote: (deviceId: number, note: string) =>
    goSolidApi.post<DeviceNote>(`/api/ecolabels/devices/${deviceId}/notes`, {
      note,
    }),

  /** Public I-REC device-register data for one device code (read-only proxy; the
   * browser cannot call Evident directly). Never a 404: `status` says what happened. */
  lookupIrecDevice: (code: string) =>
    goSolidApi.get<IrecDeviceLookupResponse>(
      `/api/ecolabels/irec-devices/${encodeURIComponent(code)}`
    ),

  getDeviceDuplicates: (deviceId: number) =>
    goSolidApi.get<{ pairs: DuplicatePair[] }>(
      `/api/ecolabels/devices/${deviceId}/duplicates`
    ),

  // Duplicate cleaner (DEV-777)
  listDuplicatePairs: (status = "PENDING", offset = 0, limit = 100) =>
    goSolidApi.get<DuplicatePairListResponse>(
      `/api/ecolabels/duplicates?status=${status}&offset=${offset}&limit=${limit}`
    ),

  triggerDuplicateScan: () =>
    goSolidApi.post<{ task_id: string }>("/api/ecolabels/duplicates/scan", {}),

  resolveDuplicatePair: (
    pairId: number,
    resolution: "not_duplicate" | "merge",
    canonicalDeviceId?: number
  ) =>
    goSolidApi.post<DuplicatePair>(
      `/api/ecolabels/duplicates/${pairId}/resolve`,
      {
        resolution,
        ...(canonicalDeviceId !== undefined
          ? { canonical_device_id: canonicalDeviceId }
          : {}),
      }
    ),

  assessDuplicatePair: (pairId: number) =>
    goSolidApi.post<{ assessment: DuplicatePair["ai_assessment"] }>(
      `/api/ecolabels/duplicates/${pairId}/assess`,
      {}
    ),

  // Export
  exportDevices: (filters?: {
    standards?: string[];
    statuses?: string[];
    countries?: string[];
    renewable_types?: string[];
    search?: string;
    device_ids?: number[];
  }) =>
    goSolidApi.post("/api/ecolabels/devices/export", filters ?? {}, {
      responseType: "blob",
    }),

  // Filters
  getCountries: () =>
    goSolidApi.get<{ countries: string[] }>("/api/ecolabels/filters/countries"),

  getRenewableTypes: () =>
    goSolidApi.get<{ renewable_types: string[] }>(
      "/api/ecolabels/filters/renewable-types"
    ),

  getStandards: () =>
    goSolidApi.get<{ standards: FilterOption[] }>(
      "/api/ecolabels/filters/standards"
    ),

  getStatuses: () =>
    goSolidApi.get<{ statuses: FilterOption[] }>(
      "/api/ecolabels/filters/statuses"
    ),

  // Document endpoints
  getDeviceDocuments: (deviceId: number) =>
    goSolidApi.get<{
      documents: {
        id: number;
        company_id: number;
        file_name: string;
        upload_source: string;
        created_at: string;
      }[];
      count: number;
    }>(`/api/ecolabels/devices/${deviceId}/documents`),

  downloadDocument: (companyId: number, uploadId: number) =>
    goSolidApi.get(`/api/company/${companyId}/download/${uploadId}`, {
      responseType: "blob",
    }),

  getDocumentCounts: (deviceIds: number[]) =>
    goSolidApi.post<{ counts: Record<number, number> }>(
      "/api/ecolabels/devices/document-counts",
      { device_ids: deviceIds }
    ),

  // AI endpoints
  autofillDevice: (companyId: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("company_id", String(companyId));
    return goSolidApi.post<{
      status: string;
      data: AiAutofillResponse;
      upload_ids: number[];
      conversation_id: number;
    }>("/api/ecolabels/ai/autofill", formData);
  },

  // --- Cancellation Statements ---

  /**
   * PUT /api/ecolabels/statements/<id>: saves the statement's details and, when given, its device
   * links. 400 for a `beneficiary_country` that is not an ISO 3166-1 alpha-2 code, and with
   * `error_type` `ECOLABEL_STATEMENT_LOCKED` while the statement belongs to an issued or voided label.
   */
  updateStatement: (statementId: number, data: UpdateStatementRequest) =>
    goSolidApi.put<CancellationStatement>(
      `/api/ecolabels/statements/${statementId}`,
      data
    ),

  /**
   * PUT /api/ecolabels/statements/<id>/devices: replaces the statement's device links. 400 with
   * `error_type` `ECOLABEL_STATEMENT_LOCKED` while the statement belongs to an issued or voided label.
   */
  updateStatementDevices: (
    statementId: number,
    deviceLinks: CancellationStatementDeviceLink[]
  ) =>
    goSolidApi.put<CancellationStatement>(
      `/api/ecolabels/statements/${statementId}/devices`,
      { device_links: deviceLinks }
    ),

  listAuthorizedSellerCompanies: () =>
    goSolidApi.get<AuthorizedSellerCompanyListResponse>(
      "/api/ecolabels/companies/authorized-sellers"
    ),
};
